"use client";

import React, { useId, useRef, useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (path: string) => void;
  onUpload: (file: File) => Promise<{ url: string }>;
  isUploading?: boolean;
  onUploadError?: (message: string) => void;
  className?: string;
  required?: boolean;
}

export function ImageUpload({
  id: idProp,
  label,
  value,
  onChange,
  onUpload,
  isUploading = false,
  onUploadError,
  className,
  required = false,
}: ImageUploadProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [value]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await onUpload(file);
      onChange(result.url);
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || "Upload failed";
      onUploadError?.(message);
    } finally {
      e.target.value = "";
    }
  };

  const handleRemove = () => {
    onChange("");
    inputRef.current?.focus();
  };

  const imageSrc = value || null;

  return (
    <div className={cn(label ? "grid gap-2" : "", className)}>
      {label ? (
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </Label>
      ) : null}
      <div
        className={cn(
          label
            ? "flex flex-col gap-3 sm:flex-row sm:items-center"
            : "flex flex-row items-center gap-2",
        )}
      >
        {imageSrc ? (
          <div className="relative shrink-0 rounded-md border border-border overflow-hidden bg-muted ring-1 ring-border/50">
            {imgError ? (
              <div className="h-24 w-24 flex items-center justify-center bg-muted">
                <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
              </div>
            ) : (
              <img
                src={imageSrc}
                alt="Preview"
                className="h-24 w-24 object-cover"
                onError={() => setImgError(true)}
              />
            )}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
          </div>
        ) : null}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <input
            ref={inputRef}
            id={id}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            disabled={isUploading}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 min-w-[100px]"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="w-3.5 h-3.5" />
                Upload
              </>
            )}
          </Button>
          {imageSrc && !isUploading && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10 hover:border-destructive"
              onClick={handleRemove}
              aria-label="Remove image"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
